/**
 * Runtime cleanup for the static SEO fallback tags shipped in index.html.
 *
 * What: Watches `<head>` for the first react-helmet-async-managed meta
 * description, then removes any tags marked `data-default-seo="true"` so
 * the page never carries both a generic and a route-specific copy.
 * Why: index.html ships defaults for non-prerendered routes and JS-disabled
 * crawlers (Slack/WhatsApp link previews). Helmet only manages tags it
 * creates (with `data-rh="true"`); without this cleanup, JS-enabled
 * crawlers see duplicate description / og / theme-color tags.
 * When: Update if the fallback marker, Helmet's marker attribute, or the
 * trigger tag (currently the meta description) changes.
 */

const DEFAULT_SEO_SELECTOR = '[data-default-seo="true"]';
const HELMET_DESCRIPTION_SELECTOR = 'meta[name="description"][data-rh="true"]';
const OBSERVER_FALLBACK_TIMEOUT_MS = 4000;

const removeDefaults = (): boolean => {
  const defaults = document.querySelectorAll(DEFAULT_SEO_SELECTOR);
  if (defaults.length === 0) return false;
  defaults.forEach(el => el.remove());
  return true;
};

export function installDefaultSeoFallbackCleanup(): void {
  if (typeof document === 'undefined') return;
  if (!document.querySelector(DEFAULT_SEO_SELECTOR)) return;

  // If Helmet already committed (e.g. prerender hydration), strip immediately.
  if (document.querySelector(HELMET_DESCRIPTION_SELECTOR)) {
    removeDefaults();
    return;
  }

  let cleared = false;
  const fallback = window.setTimeout(() => {
    if (cleared) return;
    // Helmet never committed (e.g. legal page, error route). Leave the static
    // defaults in place — they remain a valid fallback for crawlers.
    observer.disconnect();
  }, OBSERVER_FALLBACK_TIMEOUT_MS);

  const observer = new MutationObserver(() => {
    if (!document.querySelector(HELMET_DESCRIPTION_SELECTOR)) return;
    cleared = removeDefaults();
    if (cleared) {
      observer.disconnect();
      window.clearTimeout(fallback);
    }
  });

  observer.observe(document.head, { childList: true, subtree: false });
}
